import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import SpotifyButtonIdle from "@default/js/participants/components/spotify-widget/spotify-button-idle";
import SpotifyButtonListen from "@default/js/participants/components/spotify-widget/spotify-button-listen";
import CustomAnimation from "@default/general-components/custom-animation";
import useAccessibilityContext from "@default/js/accessibility/store/use-accessibility-context";

const SpotifyWidget = ({ content }) => {
  const { simpleDesign } = useAccessibilityContext();

  const [spotifyPlaying, setSpotifyPlaying] = useState(false);

  const embedControllerRef = useRef(null);

  // Update this function to include 'show' type
  const extractSpotifyData = (url) => {
    const regexArtist = /artist\/([a-zA-Z0-9]+)/,
      regexAlbum = /album\/([a-zA-Z0-9]+)/,
      regexEpisode = /episode\/([a-zA-Z0-9]+)/,
      regexShow = /show\/([a-zA-Z0-9]+)/;

    let match = url?.match(regexArtist),
      type = "artist";

    if (!match) {
      match = url?.match(regexAlbum);
      type = "album";
    }
    if (!match) {
      match = url?.match(regexEpisode);
      type = "episode";
    }
    if (!match) {
      match = url?.match(regexShow);
      type = "show"; // Add 'show' type
    }

    return {
      id: match ? match[1] : null,
      type: type,
    };
  };

  const spotifyData = content?.fieldSpotify?.uri?.path
    ? extractSpotifyData(content?.fieldSpotify?.uri?.path)
    : false;

  useEffect(() => {
    if (!spotifyData?.id) {
      return;
    }

    const initSpotify = (IFrameAPI) => {
      const element = document.getElementById("embed-iframe");
      if (!element) return;
      const options = { uri: `spotify:${spotifyData.type}:${spotifyData.id}` };
      IFrameAPI.createController(element, options, (EmbedController) => {
        embedControllerRef.current = EmbedController;
      });
    };

    if (window.SpotifyIframeAPI) {
      initSpotify(window.SpotifyIframeAPI);
      return;
    }

    if (
      !document.querySelector(
        'script[src="https://open.spotify.com/embed-podcast/iframe-api/v1"]'
      )
    ) {
      const script = document?.createElement("script");
      script.src = "https://open.spotify.com/embed-podcast/iframe-api/v1";
      script.async = true;
      document?.body.appendChild(script);
    }

    if (!window.onSpotifyIframeApiReady) {
      window.onSpotifyIframeApiReady = (IFrameAPI) => {
        window.SpotifyIframeAPI = IFrameAPI;
        initSpotify(IFrameAPI);
      };
    }

    return () => {
      embedControllerRef.current?.pause?.();
      embedControllerRef.current = null;
    };
  }, [spotifyData?.id, spotifyData?.type]);

  const togglePlay = () => {
    if (embedControllerRef.current) {
      embedControllerRef.current.togglePlay();
      setSpotifyPlaying((prev) => !prev);
    }
  };

  if (!content?.fieldSpotify) {
    return;
  }

  return (
    <div className="spotify-widget">
      <div id="embed-iframe" />
      {typeof window !== "undefined" &&
        !simpleDesign &&
        document?.querySelector(".detail-page-intro .title-wrapper h1") && (
          <>
            {createPortal(
              <button
                className="toggle-spotify"
                aria-label={"Spotify Play"}
                onClick={() => togglePlay()}
              >
                <CustomAnimation isVisible={spotifyPlaying} type={"appear"}>
                  <SpotifyButtonListen />
                </CustomAnimation>
                <CustomAnimation isVisible={!spotifyPlaying} type={"appear"}>
                  <SpotifyButtonIdle />
                </CustomAnimation>
              </button>,
              document?.querySelector(".detail-page-intro .title-wrapper h1")
            )}
          </>
        )}
    </div>
  );
};

export default SpotifyWidget;
